import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { AuthService, NotificationService } from '../../../core/services';

@Component({
  selector: 'app-check-email',
  standalone: false,
  templateUrl: './check-email.html',
  styleUrl: './check-email.scss',
})
export class CheckEmail implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  email = '';
  isResending = false;
  resendCooldown = 0;
  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.email = this.route.snapshot.queryParamMap.get('email') ?? '';
    if (!this.email) {
      // Nothing to show without knowing which address send them back to sign up properly.
      this.router.navigate(['/signup']);
    }
  }

  resend(): void {
    if (this.isResending || this.resendCooldown > 0) return;

    this.isResending = true;
    this.authService.resendConfirmation(this.email)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error('Could not resend the confirmation email. Please try again later.');
          return throwError(() => error);
        }),
        tap(result => {
          this.notificationService.success(
            result.message || 'If that address belongs to an unconfirmed account, a new confirmation email is on its way.',
          );
          this.startCooldown(30);
        }),
        finalize(() => { this.isResending = false; }),
      ).subscribe();
  }

  private startCooldown(seconds: number): void {
    this.resendCooldown = seconds;
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0 && this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  }
}
